import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Alert, Spinner, Form, Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import slotService from '../../services/slotService';
import studentService from '../../services/studentService';

const SlotSelection = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token');
    
    const [availableSlots, setAvailableSlots] = useState([]);
    const [loading, setLoading] = useState(true);
    const [booking, setBooking] = useState(false);
    const [selectedSlot, setSelectedSlot] = useState(null);
    
    // Resume upload states
    const [showResumeModal, setShowResumeModal] = useState(false);
    const [resumeFile, setResumeFile] = useState(null);
    const [resumeUploading, setResumeUploading] = useState(false);
    const [currentResumeLink, setCurrentResumeLink] = useState('');
    const [studentInfo, setStudentInfo] = useState(null);
    const [resumeRequired, setResumeRequired] = useState(false);
    const [canSelectSlots, setCanSelectSlots] = useState(false);
    
    // Token expiration states
    const [tokenExpired, setTokenExpired] = useState(false);
    const [expiredReason, setExpiredReason] = useState('');
    const [expiredData, setExpiredData] = useState(null);

    useEffect(() => {
        if (!token) {
            navigate('/error');
            return;
        }
        loadData();
    }, [token, navigate]);

    const loadData = async () => {
        try {
            const [slots, student] = await Promise.all([
                slotService.getAvailableSlots(),
                studentService.getStudentByToken(token)
            ]);
            setAvailableSlots(slots);
            setStudentInfo(student);
            setCurrentResumeLink(student.resumeLink || '');
            
            // Determine if resume is required and if slots can be selected
            const hasResume = !!(student.resumeLink && student.resumeLink.trim());
            setResumeRequired(!hasResume);
            setCanSelectSlots(hasResume);
            
            // Auto-open modal if no resume
            if (!hasResume) {
                setShowResumeModal(true);
            }
        } catch (error) {
            console.error('Error loading data:', error);
            
            // Handle expired token (410 status)
            if (error.response?.status === 410) {
                setTokenExpired(true);
                setExpiredReason(error.response.data.reason || 'Unknown reason');
                setExpiredData(error.response.data);
                toast.error('This invitation link has expired');
            } else if (error.response?.status === 404) {
                toast.error('Invalid invitation link');
                setTimeout(() => navigate('/error'), 2000);
            } else {
                const errorMessage = error.userMessage || error.response?.data?.error || error.message;
                toast.error('Failed to load data: ' + errorMessage);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleResumeFileSelect = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        try {
            studentService.validateResumeFile(file);
            setResumeFile(file);
        } catch (error) {
            toast.error(error.message);
            event.target.value = '';
        }
    };

    // In the success message after resume upload, remove any link display
const handleResumeUpload = async () => {
  if (!resumeFile) {
    toast.error('Please select a resume file first');
    return;
  }

  setResumeUploading(true);
  try {
    const result = await studentService.updateResume(token, resumeFile);
    // Don't set resumeLink as URL, just mark as uploaded
    setCurrentResumeLink('uploaded'); // Just mark as uploaded
    setResumeRequired(false);
    setCanSelectSlots(true);
    toast.success('🎉 Resume uploaded successfully! You can now select an interview slot.');
    setShowResumeModal(false);
    setResumeFile(null);
    document.getElementById('resume-upload').value = '';
  } catch (error) {
            console.error('Resume upload error:', error);
            
            // Handle expired token during resume upload
            if (error.response?.status === 410) {
                setTokenExpired(true);
                setExpiredReason(error.response.data.reason || 'Token expired during upload');
                setExpiredData(error.response.data);
                toast.error('This invitation link has expired');
                setShowResumeModal(false);
            } else {
                const errorMessage = error.userMessage || error.response?.data?.error || error.message;
                toast.error('Failed to update resume: ' + errorMessage);
            }
        } finally {
            setResumeUploading(false);
        }
    };

    const handleSlotSelection = async (slotId) => {
        // Double-check resume requirement
        if (!canSelectSlots || !currentResumeLink) {
            toast.warning('Please upload your resume before selecting a slot');
            setShowResumeModal(true);
            return;
        }

        setSelectedSlot(slotId);
        setBooking(true);
        try {
            const result = await slotService.bookSlot(slotId, token);
            toast.success('✅ Interview slot booked successfully!');
            
            // Navigate to confirmation with comprehensive data
            navigate('/student/booking-confirmation', { 
                state: { 
                    slotDateTime: result.slotDateTime,
                    meetingLink: result.meetingLink,
                    studentName: result.studentName || studentInfo.name,
                    interviewer: result.interviewer,
                    bookingId: result.bookingId,
                    tokenExpired: result.tokenExpired
                } 
            });
        } catch (error) {
            console.error('Slot booking error:', error);
            
            // Handle expired token during booking
            if (error.response?.status === 410) {
                setTokenExpired(true);
                setExpiredReason(error.response.data.reason || 'Token expired during booking');
                setExpiredData(error.response.data);
                toast.error('This invitation link has expired');
            } else {
                const errorMessage = error.userMessage || error.response?.data?.error || error.message;
                toast.error('Failed to book slot: ' + errorMessage);
            }
            setSelectedSlot(null);
        } finally {
            setBooking(false);
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const formatTime = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    };

    const isToday = (dateString) => {
        const today = new Date();
        const slotDate = new Date(dateString);
        return today.toDateString() === slotDate.toDateString();
    };

    const isTomorrow = (dateString) => {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        const slotDate = new Date(dateString);
        return tomorrow.toDateString() === slotDate.toDateString();
    };

    // Show expired token message
    if (tokenExpired) {
        return (
            <div style={{ backgroundColor: '#f8f9fa', minHeight: '100vh', paddingTop: '2rem' }}>
                <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '70vh' }}>
                    <Card className="shadow-lg border-0" style={{ maxWidth: '600px', borderRadius: '15px' }}>
                        <Card.Body className="text-center p-5">
                            <div className="mb-4">
                                <i className="bi bi-exclamation-triangle-fill" 
                                   style={{ fontSize: '4rem', color: '#dc3545' }}></i>
                            </div>
                            <h3 className="text-danger mb-3">Invitation Link Expired</h3>
                            <p className="text-muted mb-4">
                                This invitation link is no longer valid and cannot be used.
                            </p>
                            
                            <Alert variant="warning" className="mb-4 text-start">
                                <Alert.Heading className="h6">
                                    <i className="bi bi-info-circle me-2"></i>Reason:
                                </Alert.Heading>
                                <p className="mb-2">
                                    <strong>{expiredReason}</strong>
                                </p>
                                {expiredData?.expiredAt && (
                                    <small className="text-muted">
                                        Expired on: {new Date(expiredData.expiredAt).toLocaleString()}
                                    </small>
                                )}
                                {expiredData?.studentName && (
                                    <div className="mt-2">
                                        <small className="text-muted">
                                            Student: {expiredData.studentName}
                                        </small>
                                    </div>
                                )}
                            </Alert>

                            <div className="mb-4">
                                <h6 className="text-dark mb-3">What happens next?</h6>
                                <ul className="list-unstyled text-muted text-start">
                                    <li className="mb-2">
                                        <i className="bi bi-check-circle text-success me-2"></i>
                                        Your interview slot has been successfully booked
                                    </li>
                                    <li className="mb-2">
                                        <i className="bi bi-envelope text-info me-2"></i>
                                        You should have received a confirmation email with details
                                    </li>
                                    <li className="mb-2">
                                        <i className="bi bi-calendar-event text-warning me-2"></i>
                                        Please check your email for the meeting link and time
                                    </li>
                                </ul>
                            </div>

                            <div className="d-flex gap-2 justify-content-center flex-wrap">
                                <Button 
                                    variant="primary" 
                                    onClick={() => window.location.href = 'mailto:hr@company.com?subject=Interview Inquiry'}
                                >
                                    <i className="bi bi-envelope me-2"></i>
                                    Contact HR
                                </Button>
                                <Button 
                                    variant="outline-secondary" 
                                    onClick={() => window.close()}
                                >
                                    <i className="bi bi-x-circle me-2"></i>
                                    Close Window
                                </Button>
                            </div>

                            <div className="mt-4 pt-3 border-top">
                                <small className="text-muted">
                                    <i className="bi bi-info-circle me-1"></i>
                                    If you need to make changes or have questions, please contact HR.
                                </small>
                            </div>
                        </Card.Body>
                    </Card>
                </Container>
            </div>
        );
    }

    if (loading) {
        return (
            <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '70vh' }}>
                <div className="text-center">
                    <Spinner animation="border" role="status" size="lg" className="mb-3" style={{ color: '#007bff' }}>
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                    <h5 className="text-muted">Loading available slots...</h5>
                    <small className="text-muted">Verifying your invitation...</small>
                </div>
            </Container>
        );
    }

    return (
        <div style={{ backgroundColor: '#f8f9fa', minHeight: '100vh', paddingTop: '2rem', paddingBottom: '2rem' }}>
            <Container>
                <Row className="justify-content-center">
                    <Col lg={10} xl={8}>
                        {/* Header Section */}
                        <div className="text-center mb-4">
                            <div className="mb-3">
                                <i className="bi bi-calendar-check" style={{ fontSize: '3rem', color: '#007bff' }}></i>
                            </div>
                            <h2 className="fw-bold text-dark mb-2">Select Your Interview Slot</h2>
                            
                            {/* Progress Indicator */}
                            <div className="mb-4">
                                <div className="d-flex justify-content-center align-items-center mb-3">
                                    <div className={`progress-step ${canSelectSlots ? 'completed' : 'active'}`}>
                                        <div className="step-number">1</div>
                                        <div className="step-label">Upload Resume</div>
                                        {canSelectSlots && <i className="bi bi-check-circle-fill text-success"></i>}
                                    </div>
                                    <div className="progress-line"></div>
                                    <div className={`progress-step ${canSelectSlots ? 'active' : 'inactive'}`}>
                                        <div className="step-number">2</div>
                                        <div className="step-label">Select Slot</div>
                                    </div>
                                </div>
                            </div>

                            <p className="text-muted fs-5">
                                {resumeRequired ? 
                                    '📋 Step 1: Please upload your resume to continue' :
                                    '📅 Step 2: Choose your preferred interview time slot'
                                }
                            </p>
                            
                            {/* Student Info & Resume Section */}
                            {studentInfo && (
                                <Card className={`mb-4 border-0 shadow-sm ${resumeRequired ? 'border-warning' : 'border-success'}`} 
                                      style={{ borderRadius: '10px', borderWidth: '2px' }}>
                                    <Card.Body className="py-3">
                                        <Row className="align-items-center">
                                            <Col md={8}>
                                                <div className="text-start">
                                                    <h6 className="mb-1">Welcome, {studentInfo.name}</h6>
                                                    <small className="text-muted">
                                                        <i className="bi bi-envelope me-2"></i>{studentInfo.email}
                                                    </small>
                                                    {currentResumeLink ? (
                                                        <div className="mt-1">
                                                            <small className="text-success fw-bold">
                                                                <i className="bi bi-check-circle-fill me-2"></i>
                                                                Resume uploaded successfully ✓
                                                            </small>
                                                        </div>
                                                    ) : (
                                                        <div className="mt-1">
                                                            <small className="text-warning fw-bold">
                                                                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                                                                Resume upload required
                                                            </small>
                                                        </div>
                                                    )}
                                                </div>
                                            </Col>
                                            <Col md={4} className="text-end">
                                                <Button
                                                    variant={currentResumeLink ? "success" : "warning"}
                                                    size="lg"
                                                    onClick={() => setShowResumeModal(true)}
                                                    className={`fw-bold ${currentResumeLink ? "" : "pulse-animation"}`}
                                                >
                                                    <i className="bi bi-upload me-2"></i>
                                                    {currentResumeLink ? 'Update Resume' : 'Upload Resume *'}
                                                </Button>
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>
                            )}

                            {/* Resume Required Alert */}
                            {resumeRequired && (
                                <Alert variant="warning" className="mb-4 border-0 shadow-sm" style={{ borderRadius: '10px' }}>
                                    <Alert.Heading className="h5 d-flex align-items-center">
                                        <i className="bi bi-upload me-2"></i>
                                        Resume Upload Required
                                        <span className="badge bg-warning ms-2">Step 1</span>
                                    </Alert.Heading>
                                    <p className="mb-3">
                                        Please upload your resume to continue with the interview process. 
                                        This ensures our hiring team has your latest information.
                                    </p>
                                    <Button 
                                        variant="warning" 
                                        onClick={() => setShowResumeModal(true)}
                                        className="fw-bold"
                                    >
                                        <i className="bi bi-upload me-2"></i>
                                        Upload Resume Now
                                    </Button>
                                </Alert>
                            )}

                            {/* Success Alert when resume is uploaded */}
                            {canSelectSlots && !resumeRequired && (
                                <Alert variant="success" className="mb-4 border-0 shadow-sm" style={{ borderRadius: '10px' }}>
                                    <Alert.Heading className="h5 d-flex align-items-center">
                                        <i className="bi bi-check-circle-fill me-2"></i>
                                        Resume Uploaded Successfully!
                                        <span className="badge bg-success ms-2">Step 2</span>
                                    </Alert.Heading>
                                    <p className="mb-0">
                                        Great! Now you can select your preferred interview time slot from the options below.
                                    </p>
                                </Alert>
                            )}

                            <div className="mb-3">
                                <Button
                                    variant="info"
                                    className="px-3 py-2 border-0"
                                    disabled
                                >
                                    <i className="bi bi-clock me-2"></i>
                                    {availableSlots.length} Slots Available
                                </Button>
                            </div>
                        </div>

                        {/* Main Content Card */}
                        <Card className="shadow-lg border-0" style={{ borderRadius: '15px' }}>
                            <Card.Body className="p-4">
                                {availableSlots.length === 0 ? (
                                    <Alert variant="warning" className="text-center border-0 shadow-sm" style={{ borderRadius: '10px' }}>
                                        <div className="mb-3">
                                            <i className="bi bi-exclamation-triangle" style={{ fontSize: '2.5rem', color: '#856404' }}></i>
                                        </div>
                                        <Alert.Heading className="h4">No Available Slots</Alert.Heading>
                                        <p className="mb-3 fs-6">There are currently no available interview slots.</p>
                                        <hr />
                                        <p className="mb-0">
                                            <i className="bi bi-telephone me-2"></i>
                                            Please check back later or contact HR for assistance.
                                        </p>
                                    </Alert>
                                ) : (
                                    <Row className="g-4">
                                        {availableSlots.map(slot => (
                                            <Col md={6} lg={4} key={slot.Id}>
                                                <Card 
                                                    className={`h-100 slot-card ${selectedSlot === slot.Id ? 'border-primary shadow-lg' : 'border-light shadow-sm'} ${!canSelectSlots ? 'slot-disabled' : ''}`}
                                                    style={{ 
                                                        cursor: canSelectSlots ? 'pointer' : 'not-allowed',
                                                        borderRadius: '12px',
                                                        transition: 'all 0.3s ease',
                                                        transform: selectedSlot === slot.Id ? 'translateY(-5px)' : 'none',
                                                        opacity: canSelectSlots ? 1 : 0.5,
                                                        filter: canSelectSlots ? 'none' : 'grayscale(50%)'
                                                    }}
                                                    onClick={() => canSelectSlots && !booking && setSelectedSlot(slot.Id)}
                                                    onMouseEnter={(e) => {
                                                        if (canSelectSlots && !booking) {
                                                            e.currentTarget.style.transform = 'translateY(-3px)';
                                                            e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,123,255,0.15)';
                                                        }
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        if (selectedSlot !== slot.Id && !booking && canSelectSlots) {
                                                            e.currentTarget.style.transform = 'none';
                                                            e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.1)';
                                                        }
                                                    }}
                                                >
                                                    <Card.Body className="text-center p-4 position-relative">
                                                        {/* Resume Required Overlay */}
                                                        {!canSelectSlots && (
                                                            <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                                                                 style={{ backgroundColor: 'rgba(255,193,7,0.9)', borderRadius: '12px', zIndex: 1 }}>
                                                                <div className="text-center text-dark">
                                                                    <i className="bi bi-upload" style={{ fontSize: '2rem' }}></i>
                                                                    <div className="small mt-2 fw-bold">Upload Resume First</div>
                                                                </div>
                                                            </div>
                                                        )}

                                                        {/* Date Badge */}
                                                        <div className="mb-3">
                                                            {isToday(slot.SlotDateTime) && (
                                                                <span className="badge bg-success mb-2">Today</span>
                                                            )}
                                                            {isTomorrow(slot.SlotDateTime) && (
                                                                <span className="badge bg-info mb-2">Tomorrow</span>
                                                            )}
                                                        </div>

                                                        {/* Calendar Icon */}
                                                        <div className="mb-3">
                                                            <i className="bi bi-calendar-date" 
                                                               style={{ 
                                                                   fontSize: '2rem', 
                                                                   color: selectedSlot === slot.Id ? '#007bff' : '#6c757d' 
                                                               }}></i>
                                                        </div>

                                                        {/* Date */}
                                                        <h5 className="fw-bold text-dark mb-2">
                                                            {formatDate(slot.SlotDateTime)}
                                                        </h5>

                                                        {/* Time */}
                                                        <p className="text-muted mb-3 fs-5">
                                                            <i className="bi bi-clock me-2"></i>
                                                            {formatTime(slot.SlotDateTime)}
                                                        </p>

                                                        {/* Select Button */}
                                                        <Button
                                                            variant={
                                                                booking && selectedSlot === slot.Id ? 'primary' :
                                                                !canSelectSlots ? 'outline-warning' :
                                                                selectedSlot === slot.Id ? 'primary' : 'outline-primary'
                                                            }
                                                            size="lg"
                                                            className="w-100 fw-bold"
                                                            style={{ borderRadius: '8px', padding: '12px' }}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                if (!canSelectSlots) {
                                                                    setShowResumeModal(true);
                                                                } else {
                                                                    handleSlotSelection(slot.Id);
                                                                }
                                                            }}
                                                            disabled={booking}
                                                        >
                                                            {booking && selectedSlot === slot.Id ? (
                                                                <>
                                                                    <Spinner
                                                                        as="span"
                                                                        animation="border"
                                                                        size="sm"
                                                                        role="status"
                                                                        aria-hidden="true"
                                                                        className="me-2"
                                                                    />
                                                                    Booking...
                                                                </>
                                                            ) : !canSelectSlots ? (
                                                                <>
                                                                    <i className="bi bi-upload me-2"></i>
                                                                    Upload Resume First
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <i className="bi bi-check-circle me-2"></i>
                                                                    Select This Slot
                                                                </>
                                                            )}
                                                        </Button>
                                                    </Card.Body>
                                                </Card>
                                            </Col>
                                        ))}
                                    </Row>
                                )}
                            </Card.Body>
                        </Card>

                        {/* Help Section */}
                        <Card className="mt-4 border-0 shadow-sm" style={{ borderRadius: '10px', backgroundColor: '#f8f9fa' }}>
                            <Card.Body className="text-center py-3">
                                <small className="text-muted">
                                    <i className="bi bi-info-circle me-2"></i>
                                    Need help? Contact us at <strong>hr@b2world.in</strong> or call <strong>(+91) 7482909107</strong>
                                </small>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>

            {/* Resume Upload Modal */}
            <Modal 
                show={showResumeModal} 
                onHide={() => !resumeRequired && setShowResumeModal(false)} 
                centered
                backdrop={resumeRequired ? 'static' : true}
                keyboard={!resumeRequired}
                size="lg"
            >
                <Modal.Header closeButton={!resumeRequired}>
                    <Modal.Title className="d-flex align-items-center">
                        <i className="bi bi-file-earmark-arrow-up me-2"></i>
                        {resumeRequired ? 'Resume Upload Required' : 'Update Resume'}
                        {resumeRequired && <span className="badge bg-danger ms-2">Step 1</span>}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {resumeRequired ? (
                        <Alert variant="info" className="mb-3">
                            <i className="bi bi-info-circle me-2"></i>
                            <strong>Resume upload is mandatory.</strong> Please upload your resume to proceed with slot selection.
                        </Alert>
                    ) : currentResumeLink ? (
                        <Alert variant="success" className="mb-3">
                            <i className="bi bi-check-circle me-2"></i>
                            You have a resume uploaded. Uploading a new one will replace the existing file.
                        </Alert>
                    ) : null}

                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold">Select Resume File *</Form.Label>
                            <Form.Control
                                id="resume-upload"
                                type="file"
                                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                onChange={handleResumeFileSelect}
                                required={resumeRequired}
                                size="lg"
                            />
                            <Form.Text className="text-muted">
                                📄 Supported formats: PDF, DOC, DOCX (Max size: 5MB)
                            </Form.Text>
                        </Form.Group>

                        {resumeFile && (
                            <Alert variant="success" className="mb-3">
                                <strong>✅ Selected file:</strong> {resumeFile.name}
                                <br />
                                <small>📏 Size: {studentService.formatFileSize(resumeFile.size)}</small>
                            </Alert>
                        )}
                    </Form>
                </Modal.Body>
                <Modal.Footer className="d-flex justify-content-between">
                    <div>
                        {!resumeRequired && (
                            <Button variant="outline-secondary" onClick={() => setShowResumeModal(false)} disabled={resumeUploading}>
                                Cancel
                            </Button>
                        )}
                    </div>
                    <Button 
                        variant="success"
                        size="lg"
                        onClick={handleResumeUpload}
                        disabled={!resumeFile || resumeUploading}
                        className="fw-bold"
                    >
                        {resumeUploading ? (
                            <>
                                <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                    className="me-2"
                                />
                                Uploading...
                            </>
                        ) : (
                            <>
                                <i className="bi bi-upload me-2"></i>
                                {resumeRequired ? '🚀 Upload Resume & Continue' : 'Update Resume'}
                            </>
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Custom CSS for animations and progress steps */}
            <style jsx>{`
                .pulse-animation {
                    animation: pulse 2s infinite;
                }
                
                @keyframes pulse {
                    0% { transform: scale(1); }
                    50% { transform: scale(1.05); }
                    100% { transform: scale(1); }
                }
                
                .progress-step {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    position: relative;
                }
                
                .progress-step .step-number {
                    width: 40px;
                    height: 40px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: bold;
                    margin-bottom: 8px;
                }
                
                .progress-step.active .step-number {
                    background-color: #007bff;
                    color: white;
                }
                
                .progress-step.completed .step-number {
                    background-color: #28a745;
                    color: white;
                }
                
                .progress-step.inactive .step-number {
                    background-color: #e9ecef;
                    color: #6c757d;
                }
                
                .progress-step .step-label {
                    font-size: 0.9rem;
                    font-weight: 600;
                }
                
                .progress-line {
                    width: 100px;
                    height: 2px;
                    background-color: #e9ecef;
                    margin: 0 20px;
                    margin-top: 20px;
                }
                
                .slot-disabled {
                    position: relative;
                }
            `}</style>
        </div>
    );
};

export default SlotSelection;
